import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PrintIcon from "@mui/icons-material/Print";
import HomeIcon from "@mui/icons-material/Home";
import "../../styles/questionpaper.css";
import QuestionRenderer from "./QuestionRenderer";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { convertMathJax } from "../../utils/QuestionsUtils";

const letterChoices = ["A", "B", "C", "D", "E", "F"];

const QuestionPaper = ({ questionsData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subjectName } = location.state || {};

  const handlePrint = () => {
    const printContent = document.querySelector(".question-paper-container");

    // Gather all usable styles
    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join("\n");
        } catch {
          return ""; // Skip external stylesheets (CORS)
        }
      })
      .join("\n");

    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.write("<html><head><title>Print</title>");
    printWindow.document.write(`
      <style>
        ${styles}
        @media print {
          body {
            margin: 0;
            padding: 20px;
            font-family: sans-serif;
          }
          .question-paper-container {
            page-break-before: avoid;
          }
          h1, .paper-header {
            page-break-before: auto;
            page-break-after: avoid;
          }
        }
      </style>
    `);
    printWindow.document.write("</head><body>");
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <>
      <div className="top-toolbar">
        <button onClick={handlePrint} className="icon-button" title="Print">
          <PrintIcon />
        </button>
        <button onClick={() => navigate("/home")} className="icon-button" title="Home">
          <HomeIcon />
        </button>
      </div>

      <div className="question-paper-container">
        <div className="paper-header">
          <h1>{subjectName || "Question Paper"}</h1>
          <p>
            <strong>Instructions:</strong> Read each question carefully and choose the correct option.
          </p>
        </div>

        <div className="paper-body">
          {questionsData.map((question, index) => {
            const sortedChoices = question?.answerChoices?.slice().sort((a, b) => a.id - b.id);

            return (
              <div key={question.id} className="question-item">
                <QuestionRenderer question={question} index={index} />

                {sortedChoices?.length > 0 && (
                  <table className="options-grid">
                    <tbody>
                      {sortedChoices.reduce((rows, choice, idx) => {
                        if (idx % 2 === 0) {
                          rows.push([choice]);
                        } else {
                          rows[rows.length - 1].push(choice);
                        }
                        return rows;
                      }, []).map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((choice, colIndex) => (
                            <td key={choice.id} className="option-cell">
                              <MathJaxContext>
                                <strong>
                                  {letterChoices[rowIndex * 2 + colIndex] ||
                                    String.fromCharCode(65 + rowIndex * 2 + colIndex)}.
                                </strong>

                                {' '}
                                <span>
                                  {convertMathJax(choice.choiceText)}</span>
                              </MathJaxContext>
                            </td>
                          ))}
                          {row.length < 2 && <td className="option-cell" />} {/* Fill empty cell */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
        <div className="answer-key">
          <h2>Answer Key</h2>
          <ol>
            {questionsData.map((question, index) => {
              const correct = question.answerChoices.find(c => c.isCorrect);
              return (
                <li key={question.id}>
                  <strong>
                    <MathJaxContext>
                      {correct?.choiceText && convertMathJax(correct?.choiceText)}
                      {!correct?.choiceText && "N/A"}
                    </MathJaxContext>
                  </strong>
                </li>
              );
            })}
          </ol>
        </div>
      </div>


    </>
  );
};

export default QuestionPaper;
